import { Menu, X } from "lucide-react";
import { Dialog } from "radix-ui";
import { useEffect, useState, type ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation, useMatch, useResolvedPath } from "react-router";

import { prefetchPages } from "@/app/pages";
import { NAV, ROUTES, type NavItem } from "@/app/routes";
import { useSession } from "@/core/auth/session";
import { useSidebar } from "@/core/layout/use-sidebar";
import { Button } from "@/ui/button";
import { cn } from "@/ui/cn";
import { EnvironmentTag } from "@/ui/environment-tag";
import { Logo, LogoMark } from "@/ui/logo";
import { Tip, TooltipProvider } from "@/ui/tooltip";

import { AccountMenu } from "./AccountMenu";
import { SidebarEdge } from "./SidebarEdge";

/**
 * One menu link, highlighted on its page. The class is worked out here (not NavLink's class function), because
 * the tooltip wrapper merges class names as text and would turn a function into nonsense.
 */
function MenuLink({
  item: { label, to, icon: Icon },
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const end = to === ROUTES.overview;
  const active = useMatch({ path: useResolvedPath(to).pathname, end }) !== null;
  return (
    <Tip label={label} enabled={collapsed}>
      <NavLink
        to={to}
        end={end}
        onClick={onNavigate}
        className={cn(
          "flex min-h-10 items-center gap-3 rounded-[10px] text-sm font-medium no-underline transition-colors lg:short:min-h-9",
          collapsed ? "justify-center px-0" : "px-3",
          active
            ? "bg-primary-soft font-semibold text-primary-soft-ink"
            : "text-ink-2 hover:bg-surface-muted hover:text-ink",
        )}
      >
        <Icon className="size-[18px] shrink-0" aria-hidden="true" />
        <span className={collapsed ? "sr-only" : "truncate"}>{label}</span>
      </NavLink>
    </Tip>
  );
}

/**
 * The menu scrolls on its own, so the logo and the account card stay in view on short screens. Collapsed: icons
 * only, each named in a tooltip (and to screen readers); sections become thin dividers.
 */
function Navigation({ onNavigate, collapsed = false }: { onNavigate?: () => void; collapsed?: boolean }) {
  return (
    <nav
      aria-label="Main"
      data-collapsed={collapsed || undefined}
      className="scroll-shadows -mx-1 flex min-h-0 grow flex-col gap-6 overflow-y-auto px-1 lg:short:gap-5"
    >
      {NAV.map((group, index) => (
        <div key={group.section ?? index} className="flex flex-col gap-0.5">
          {group.section &&
            (collapsed ? (
              <span className="mx-auto mb-2 h-px w-6 shrink-0 bg-border">
                <span className="sr-only">{group.section}</span>
              </span>
            ) : (
              <span className="truncate px-3 pb-2 text-[11px] font-bold tracking-[0.08em] text-faint uppercase">
                {group.section}
              </span>
            ))}
          {group.items.map((item) => (
            <MenuLink key={item.to} item={item} collapsed={collapsed} onNavigate={onNavigate} />
          ))}
        </div>
      ))}
    </nav>
  );
}

function AccountCard({ collapsed = false }: { collapsed?: boolean }) {
  const { isSuperAdmin } = useSession();
  return <AccountMenu rail={collapsed} subtitle={isSuperAdmin ? "Super admin" : "Admin"} />;
}

/** The Brocoly logo: back to Overview, the portal's main page. */
function HomeLink({ children }: { children: ReactNode }) {
  return (
    <Link
      to={ROUTES.overview}
      aria-label="Brocoly Admin: go to Overview"
      className="rounded-xl text-ink no-underline"
    >
      {children}
    </Link>
  );
}

/** Sidebar on desktop (collapsible to icons); a top bar with a slide-in menu below 1024 px. */
export function AppShell() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const sidebar = useSidebar();
  const collapsed = sidebar.collapsed;
  // Once the first screen is up, get the others' code ready in the background.
  useEffect(prefetchPages, []);

  return (
    <TooltipProvider>
      <div
        className={cn(
          "min-h-dvh bg-bg transition-[grid-template-columns] duration-200 ease-out lg:grid",
          collapsed ? "lg:grid-cols-[72px_minmax(0,1fr)]" : "lg:grid-cols-[248px_minmax(0,1fr)]",
        )}
      >
        <a
          href="#main"
          className="sr-only z-50 rounded-lg bg-surface px-4 py-2 font-bold focus:not-sr-only focus:fixed focus:top-3 focus:left-3"
        >
          Skip to content
        </a>

        <aside
          id="sidebar"
          aria-label="Sidebar"
          className="group/sidebar sticky top-0 hidden h-dvh border-r border-border bg-surface lg:block"
        >
          <SidebarEdge collapsed={collapsed} onToggle={sidebar.toggle} />
          {/* Clips the content while the width animates; the toggle sits outside it, on the edge. */}
          <div
            className={cn(
              "flex h-full flex-col gap-6 overflow-hidden py-5 short:gap-4 short:py-4",
              collapsed ? "px-3" : "px-4",
            )}
          >
            {collapsed ? (
              <div className="flex shrink-0 flex-col items-center gap-2">
                <HomeLink>
                  <LogoMark />
                </HomeLink>
                <EnvironmentTag className="px-1.5 py-0 text-[10px]" />
              </div>
            ) : (
              <div className="flex shrink-0 items-center justify-between px-2">
                <HomeLink>
                  <Logo subtitle="Admin" />
                </HomeLink>
                <EnvironmentTag />
              </div>
            )}
            <Navigation collapsed={collapsed} />
            <div className={cn("shrink-0", collapsed && "flex justify-center")}>
              <AccountCard collapsed={collapsed} />
            </div>
          </div>
        </aside>

        <Dialog.Root open={menuOpen} onOpenChange={setMenuOpen}>
          <header className="sticky top-0 z-30 flex items-center justify-between border-b border-border bg-surface px-4 py-3 lg:hidden">
            <HomeLink>
              <Logo subtitle="Admin" />
            </HomeLink>
            <div className="flex items-center gap-2">
              <EnvironmentTag />
              <Dialog.Trigger asChild>
                <Button variant="secondary" size="icon" aria-label="Open menu">
                  <Menu aria-hidden="true" />
                </Button>
              </Dialog.Trigger>
            </div>
          </header>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40 lg:hidden" />
            <Dialog.Content
              key={location.pathname}
              className="fixed inset-y-0 left-0 z-50 flex w-[86%] max-w-80 flex-col gap-6 bg-surface px-4 py-5 lg:hidden"
            >
              <div className="flex shrink-0 items-center justify-between px-2">
                <Dialog.Title asChild>
                  <span>
                    <Logo subtitle="Admin" />
                  </span>
                </Dialog.Title>
                <Dialog.Close asChild>
                  <Button variant="quiet" size="icon" aria-label="Close menu">
                    <X aria-hidden="true" />
                  </Button>
                </Dialog.Close>
              </div>
              <Dialog.Description className="sr-only">Pages of the admin portal</Dialog.Description>
              <Navigation onNavigate={() => setMenuOpen(false)} />
              <div className="shrink-0">
                <AccountCard />
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>

        <main id="main" tabIndex={-1} className="flex min-w-0 flex-col gap-6 px-4 pt-6 pb-10 sm:px-8 sm:pt-7">
          <Outlet />
        </main>
      </div>
    </TooltipProvider>
  );
}
